import { Box } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { useGetAgencyOrganizations } from '@/apis/agency/organizations/hook';
import { useArchiveAgencyOnboardingForm, useGetAgencyOnboardingForms } from '@/apis/agency/businessOnboarding/hooks';
import { OnboardingFormResponse } from '@/apis/agency/businessOnboarding/types';
import { useToast } from '@/hooks/toast';
import { formatDate } from '@/utils/helperFunctions';

import CopyOnboardingUrlModal from './components/CopyOnboardingUrlModal';
import GenerateOnboardingFormDrawer from './components/GenerateOnboardingFormDrawer';
import OnboardingFilters from './components/OnboardingFilters';
import OnboardingHeader, { copyOnboardingLink, OnboardingViewMode } from './components/OnboardingHeader';
import OnboardingInsights from './components/OnboardingInsights';
import OnboardingListing from './components/OnboardingListing';
import SetupBusinessModal from './components/SetupBusinessModal';
import { buildOnboardingFilters, OnboardingListFormValues, OnboardingStatus, OnboardingTableRow } from './constants';
import { usePagination } from '@/components/Common/Pagination';

const defaultDateRange = `${dayjs().subtract(30, 'day').format('YYYY-MM-DD')},${dayjs().format('YYYY-MM-DD')}`;

export default function OnboardingForms() {
  const { ToastComponent, showToast } = useToast();
  const { data: agencyOrganization } = useGetAgencyOrganizations();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode: OnboardingViewMode = searchParams.get('view') === 'insights' ? 'insights' : 'list';

  const [searchText, setSearchText] = useState<string | null>(null);
  const { currentPage, perPage, setPage, setPerPage, resetPage } = usePagination();
  const [openSetupModal, setOpenSetupModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState<{
    id: number;
    businessName: string;
    status: OnboardingStatus;
    businessPlanId?: string | number | null;
    voiceEngineId?: number | null;
    overallProgress?: number;
  } | null>(null);
  const [openGenerateFormDrawer, setOpenGenerateFormDrawer] = useState(false);
  const [copyLinkModal, setCopyLinkModal] = useState<{
    businessName: string;
    contactPerson: string;
    onboardingUrl: string;
  } | null>(null);

  const { control, watch } = useForm<OnboardingListFormValues>({
    defaultValues: {
      date: defaultDateRange,
      status: '',
      source: '',
      business_state: 'active'
    }
  });

  const { status: statusFilter, source: sourceFilter, business_state: businessStateFilter } = watch();

  const apiFilters = useMemo(
    () =>
      buildOnboardingFilters({
        searchText,
        status: statusFilter,
        source: sourceFilter,
        businessState: businessStateFilter
      }),
    [searchText, statusFilter, sourceFilter, businessStateFilter]
  );

  useEffect(() => {
    resetPage();
  }, [searchText, statusFilter, sourceFilter, businessStateFilter, resetPage]);

  const { data, isLoading } = useGetAgencyOnboardingForms(
    currentPage,
    { ...apiFilters, per_page: perPage },
    {
      enabled: viewMode === 'list'
    }
  );
  const { mutate: archiveOnboardingForm } = useArchiveAgencyOnboardingForm();

  const tableData: OnboardingTableRow[] = useMemo(
    () =>
      data?.data?.map((form: OnboardingFormResponse) => ({
        id: form.id,
        business_name: form.business_legal_name,
        primary_contact: form.primary_contact_name,
        website: form.website_url,
        created_by: form.created_by?.name || 'N/A',
        created_on: formatDate(form.created_at, 'MMM D, YYYY hh:mm A'),
        updated_at: formatDate(form.updated_at, 'MMM D, YYYY hh:mm A'),
        status: form.status as OnboardingStatus,
        source: form.source || '',
        ticket_reference: form.ticket_reference || '',
        business_plan_id: form.business_plan_id,
        voice_engine_id: form.voice_engine_id,
        form_token: form?.form_token,
        overall_progress: form.step_config?.overall_progress,
        discarded_at: form.discarded_at,
        days_in_stage: form.stage_entered_at ? dayjs().diff(dayjs(form.stage_entered_at), 'day') : null,
        connected_integrations: form.connected_integrations || []
      })) || [],
    [data?.data]
  );

  const totalPages = data?.meta?.total_pages || 1;

  const handleViewModeChange = (mode: OnboardingViewMode) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (mode === 'insights') next.set('view', 'insights');
        else next.delete('view');
        return next;
      },
      { replace: true }
    );
  };

  return (
    <>
      <Box sx={{ width: '100%', pb: { xs: 28, sm: 0 } }}>
        <OnboardingHeader
          control={control}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          createdAt={agencyOrganization?.created_at}
        />

        {viewMode === 'list' && (
          <>
            <OnboardingFilters
              control={control}
              searchText={searchText}
              setSearchText={setSearchText}
              onCopyLink={() => copyOnboardingLink(showToast)}
            />
            <OnboardingListing
              data={tableData}
              isLoading={isLoading}
              totalPages={totalPages}
              totalCount={data?.meta?.total_count ?? 0}
              currentPage={currentPage}
              perPage={perPage}
              onPageChange={setPage}
              onPerPageChange={setPerPage}
              onOpenSetup={(form) => {
                setSelectedForm(form);
                setOpenSetupModal(true);
              }}
              onCopyLink={setCopyLinkModal}
              onArchive={archiveOnboardingForm}
            />
          </>
        )}

        {viewMode === 'insights' && <OnboardingInsights />}
      </Box>

      <SetupBusinessModal
        open={openSetupModal}
        onClose={() => {
          setOpenSetupModal(false);
          setSelectedForm(null);
        }}
        status={selectedForm?.status || 'draft'}
        businessName={selectedForm?.businessName || ''}
        formId={selectedForm?.id || 0}
        businessPlanId={selectedForm?.businessPlanId}
        voiceEngineId={selectedForm?.voiceEngineId}
        overallProgress={selectedForm?.overallProgress}
      />
      <GenerateOnboardingFormDrawer open={openGenerateFormDrawer} onClose={() => setOpenGenerateFormDrawer(false)} />
      <CopyOnboardingUrlModal
        open={!!copyLinkModal}
        onClose={() => setCopyLinkModal(null)}
        businessName={copyLinkModal?.businessName || ''}
        contactPerson={copyLinkModal?.contactPerson || ''}
        onboardingUrl={copyLinkModal?.onboardingUrl || ''}
      />
      {ToastComponent}
      <Outlet />
    </>
  );
}
